import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  type ExchangeRate,
  RXDB_EXCHANGE_RATE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/exchange-rate";
import type { BillingCurrency } from "elephant-web/gql";
import { createContext, useContext, useMemo } from "react";
import { useRxData } from "rxdb-hooks";
import {
  type LatestExchangeRate,
  latestExchangeRateFor,
} from "./use-latest-exchange-rate";

const ExchangeRateContext = createContext<ExchangeRate[] | null>(null);

const NO_EXCHANGE_RATES: ExchangeRate[] = [];

const FlagOnProvider: FCwC = ({ children }) => {
  const { result } = useRxData<ExchangeRate>(
    RXDB_EXCHANGE_RATE_COLLECTION_NAME,
    (collection) => collection.find(),
  );

  return (
    <ExchangeRateContext.Provider value={result}>
      {children}
    </ExchangeRateContext.Provider>
  );
};

export const ExchangeRateProvider: FCwC = ({ children }) => {
  const [multiCurrencyFlag] = useFeatureFlag(
    featureFlags.billing.multiCurrency,
  );

  if (!multiCurrencyFlag) {
    return (
      <ExchangeRateContext.Provider value={NO_EXCHANGE_RATES}>
        {children}
      </ExchangeRateContext.Provider>
    );
  }

  return <FlagOnProvider>{children}</FlagOnProvider>;
};

export const useLatestExchangeRate = (
  currency: BillingCurrency,
): LatestExchangeRate | null => {
  const rates = useContext(ExchangeRateContext);
  const latest = useMemo(
    () => rates && latestExchangeRateFor(rates, currency),
    [rates, currency],
  );
  if (!rates) {
    throw new Error(
      "useLatestExchangeRate must be used within an ExchangeRateProvider.",
    );
  }
  return latest;
};
